import { clamp } from 'lodash-es';
import type { ForwardedRef } from 'react';
import { forwardRef, useEffect, useRef } from 'react';

import { useMergedRef } from '@/hook';
import { setStyleProperties } from '@/util';

import { VFlex } from '../VFlex';
import { joinCn } from '../util';
import {
  VCircleProgressBarBorderWidth,
  VCircleProgressBarSize,
  VCircleProgressBarVariant,
  VCircleProgressBarWidth,
} from './constant';
import style from './style';
import type { VCircleProgressBarProps } from './type';
import { getDegFromValue } from './util';

const VCircleProgressBar = (
  {
    className,
    size = VCircleProgressBarSize.MEDIUM,
    value,
    variant = VCircleProgressBarVariant.IN_PROGRESS,
    renderValue,
  }: VCircleProgressBarProps,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const $el = useRef<HTMLDivElement>(null);
  const innerValue = clamp(value ?? 0, 0, 100);
  const width = VCircleProgressBarWidth[size];
  const borderWidth = VCircleProgressBarBorderWidth[size];
  const hiddenValue =
    width <= VCircleProgressBarWidth[VCircleProgressBarSize.SMALL];
  const mergedRef = useMergedRef($el, ref as any);

  useEffect(() => {
    if (!$el.current) return;

    const [degAfter, degBefore] = [
      getDegFromValue(innerValue),
      getDegFromValue(innerValue, 50),
    ];

    setStyleProperties($el.current, {
      '--width': `${width}px`,
      '--borderWidth': `${borderWidth}px`,
      '--degAfter': `${Math.min(degAfter, 0)}deg`,
      '--degBefore': `${Math.min(degBefore, 0)}deg`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerValue, size]);

  return (
    <div
      ref={mergedRef}
      className={joinCn(style.vCircleProgressBar, className)}
      role="progressbar"
    >
      {innerValue > 0 && (
        <div
          className={style.vCircleProgressBarAfter(variant)}
          role="presentation"
          data-testid="v-circle-progress-bar-after"
        />
      )}
      {innerValue > 50 && (
        <div
          className={style.vCircleProgressBarBefore(variant)}
          role="presentation"
          data-testid="v-circle-progress-bar-before"
        />
      )}
      <VFlex
        justify="center"
        items="center"
        className={style.vCircleProgressBarBackground}
        role="presentation"
      >
        {!hiddenValue ? (
          <span
            className={style.vCircleProgressBarValueText({ size, variant })}
          >
            {renderValue
              ? renderValue(innerValue)
              : `${+innerValue.toFixed(2)}%`}
          </span>
        ) : (
          ''
        )}
      </VFlex>
      <div
        className={style.vCircleProgressBarOverlay}
        role="presentation"
      ></div>
    </div>
  );
};

export default forwardRef(VCircleProgressBar);
